/**
 * WIZARD-SIDE-PANEL
 * Panneau lateral pleine hauteur pour editer une fiche a cote de la page : un entete qui la nomme
 * et la situe, ses etapes en cartes dont une seule s'ouvre a la fois, et son bouton.
 * L'etape ouverte est portee par l'appelant : c'est lui qui enchaine les etapes.
 * La carte ouverte prend la hauteur restante : son bas est donc un endroit stable pour un bouton.
 * Le bouton se pose au choix de l'appelant : dans la carte ouverte (stepFooter) pour un parcours
 * lineaire, ou dans un pied fixe (footer) pour une fiche qu'on retouche etape par etape.
 */
import { ReactNode, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { faCheck, faChevronDown, faXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { SlideIn } from "@/utils/animations.utils"
import Space from "./space"
import Tooltip from "./tooltip"
import getColor from "@/utils/get-color.utils"

//Au-dessus des modales classiques (998) : le panneau peut s'ouvrir depuis l'une d'elles.
//Exporte pour que ses propres modales filles sachent a quelle hauteur passer devant lui.
export const WIZARD_SIDE_PANEL_Z_INDEX = 999
const DEFAULT_WIDTH = 560

interface WizardSidePanelStep {
  content: ReactNode
  id: string
  isChecked?: boolean //Etape explicitement confirmee dans le parcours
  lockReason?: string //Etape inaccessible, avec le motif affiche au survol
  summary?: ReactNode //Rappel de la saisie, sur la ligne de l'etape fermee
  summaryPosition?: "side" | "below"
  title: string
}

interface Props {
  //Parcours lineaire : refermer l'etape ouverte laisserait un panneau sans le moindre bouton
  context?: ReactNode //Ce que l'entete rappelle sous le titre : l'element concerne, par exemple
  footer?: ReactNode //Boutons du pied fixe, alignes a droite. Absent : pas de pied du tout.
  footerStart?: ReactNode //Boutons du pied alignes a gauche : suppression, options
  stepFooter?: ReactNode //Bouton pose au bas de la carte ouverte, pour enchainer les etapes
  onClose: () => void
  onOpenStepChange: (id: string | null) => void
  openStepId: string | null
  steps: WizardSidePanelStep[]
  title: string
}

export default function WizardSidePanel({
  context,
  footer,
  footerStart,
  onClose,
  onOpenStepChange,
  openStepId,
  stepFooter,
  steps,
  title
}: Props) {
  const isFooterVisible = Boolean(footer || footerStart)

  //Pas de fermeture au clic sur le fond : le panneau porte un brouillon, on ne le perd pas par megarde
  return createPortal(
    <div
      className="fixed inset-0 flex justify-end"
      style={{ backgroundColor: "#00000040", zIndex: WIZARD_SIDE_PANEL_Z_INDEX }}>
      <SlideIn className="flex h-full">
        <div
          className="flex h-full flex-col bg-white shadow-custom"
          style={{ maxWidth: "100vw", width: DEFAULT_WIDTH }}>
          {/* La croix ne partage sa ligne qu'avec le titre : le contexte occupe toute la largeur */}
          <div className="flex shrink-0 flex-col border-b border-slate-100 px-[30px] pb-20 pt-[30px]">
            <div className="flex items-start gap-10">
              <h1 className="mt-0 min-w-0 flex-1 leading-tight text-slate-900">{title}</h1>

              <button
                type="button"
                className="_hover -mt-10 border-0 bg-transparent p-0 text-[38px] leading-none text-slate-500"
                onClick={onClose}>
                ×
              </button>
            </div>

            {context && <div className="mt-20">{context}</div>}
          </div>

          {/* Sans pied fixe, les cartes ne doivent pas toucher le bas du panneau */}
          <div
            className={`flex min-h-0 flex-1 flex-col gap-[10px] overflow-y-auto px-[30px] ${
              isFooterVisible ? "" : "pb-[30px]"
            }`}>
            {steps.map((step) => (
              <WizardStepCard
                key={step.id}
                footer={step.id === openStepId ? stepFooter : undefined}
                isOpen={step.id === openStepId}
                step={step}
                onToggle={() => onOpenStepChange(step.id === openStepId ? null : step.id)}
              />
            ))}
          </div>

          {isFooterVisible && (
            <div className="flex shrink-0 items-center border-t border-slate-100 px-[30px] py-20">
              {footerStart}
              <Space />
              {footer}
            </div>
          )}
        </div>
      </SlideIn>
    </div>,
    document.body
  )
}

function WizardStepCard({
  footer,
  isOpen,
  onToggle,
  step
}: {
  footer?: ReactNode
  isOpen: boolean
  onToggle: () => void
  step: WizardSidePanelStep
}) {
  const isLocked = Boolean(step.lockReason)
  const cardRef = useRef<HTMLElement>(null)
  //Etape ouverte qu'on ne peut pas refermer : son entete n'est pas un bouton, et pas de chevron
  const isToggleDisabled = isLocked

  useEffect(() => {
    //Inclure l'etape deja ouverte au montage, notamment lors d'un acces direct depuis une mesure.
    if (!isOpen || !footer) return

    //Le scroll suit la croissance de la carte pendant sa transition de 300 ms : ouverture et
    //deplacement ne forment ainsi qu'un seul mouvement, sans second scroll une fois la carte ouverte.
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const startedAt = performance.now()
    let frame = 0

    function followCardBottom(now: number) {
      cardRef.current?.scrollIntoView({ behavior: "auto", block: "end" })
      if (!prefersReducedMotion && now - startedAt < 320) {
        frame = requestAnimationFrame(followCardBottom)
      }
    }

    frame = requestAnimationFrame(followCardBottom)

    return () => cancelAnimationFrame(frame)
    //Sur [isOpen] seul : footer est un ReactNode recree a chaque rendu, le suivre relancerait
    //l'effet en continu et annulerait le scroll en cours des la premiere frappe dans le formulaire
  }, [isOpen])

  const card = (
    <section
      ref={cardRef}
      //Fermee : toute la carte est cliquable, donc _hover dessus. Ouverte, il passerait la moitie
      //du formulaire en opacite reduite : c'est l'entete qui le porte.
      //Ouverte, la carte prend la place restante (grow anime, jamais reduite sous son contenu :
      //un formulaire plus haut que la fenetre fait defiler la colonne).
      className={`relative flex shrink-0 flex-col overflow-hidden rounded-[12px] transition-[flex-grow] duration-300 ease-out ${
        isOpen ? "grow" : "grow-0"
      } ${isLocked ? "opacity-50" : isOpen ? "" : "_hover"}`}
      style={{ border: "1px solid #E2E8F0" }}>
      {step.isChecked && (
        <div
          aria-hidden
          className="absolute bottom-0 left-0 top-0 w-[8px]"
          style={{ backgroundColor: getColor("blue") }}
        />
      )}
      <div
        role={isToggleDisabled ? undefined : "button"}
        tabIndex={isToggleDisabled ? undefined : 0}
        className={`box-border flex w-full shrink-0 flex-col text-left ${
          isToggleDisabled ? "cursor-default" : isOpen ? "_hover" : ""
        }`}
        onKeyDown={
          isToggleDisabled
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  onToggle()
                }
              }
        }
        onClick={isToggleDisabled ? undefined : onToggle}>
        <div
          className={`box-border flex h-[62px] w-full shrink-0 items-center gap-10 px-20 ${
            step.isChecked ? "pl-[28px]" : ""
          }`}>
          <div className="min-w-0 shrink-0 text-lg text-slate-700">{step.title}</div>

          {/* Toujours rendu : c'est lui qui garde le chevron a droite, resume ou pas */}
          <div className="grey-t min-w-0 flex-1 truncate text-right text-base">
            {!isOpen && step.summaryPosition !== "below" && step.summary}
          </div>

          <FontAwesomeIcon
            icon={isOpen ? faXmark : step.isChecked ? faCheck : faChevronDown}
            fixedWidth
            className="shrink-0 text-[16px]"
            style={{ color: !isOpen && step.isChecked ? getColor("blue") : "#64748B" }}
          />
        </div>

        {!isOpen && step.summaryPosition === "below" && (
          <div className={`box-border w-full min-w-0 px-20 pb-20 ${step.isChecked ? "pl-[28px]" : ""}`}>
            {step.summary}
          </div>
        )}
      </div>

      {/* Fermee : grid-rows-[0fr] collapse VRAIMENT le contenu a 0 (sinon la carte fermee garderait
          la hauteur naturelle du contenu cache). */}
      <div
        className={`grid min-h-0 grow transition-[grid-template-rows,opacity] duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}>
        {/* invisible : le contenu reste monte pour l'animation, mais sort du parcours au clavier */}
        <div className={`overflow-hidden ${isOpen ? "" : "invisible"}`}>
          {/* mt-auto sur le bouton : il reste au bas de la carte, meme etiree */}
          <div className="box-border flex h-full flex-col px-20 pb-20">
            {step.content}
            {footer && <div className="mt-auto flex justify-end pt-20">{footer}</div>}
          </div>
        </div>
      </div>
    </section>
  )

  //usePortal : le panneau est en overflow hidden, l'infobulle serait rognee
  return isLocked ? (
    <Tooltip className="w-full shrink-0" text={step.lockReason} usePortal>
      {card}
    </Tooltip>
  ) : (
    card
  )
}
